/**
 * Toggle runtime state - the PWA-side mirror of OPBridge's `/toggles/{consumer}`
 * `state` field per toggle-mode FireButton. Components read from here to
 * drive lit-state instead of a local signal, so a pedal-fired flip (tab
 * closed) is visible the moment the tab reconnects.
 *
 * Mirrors `sequencer/runtime.ts` in shape.
 */

import { createSignal, type Accessor } from 'solid-js';

const [statesSignal, setStatesSignal] = createSignal<Record<string, boolean>>({});
/** Per computer (row id): a toggle lives on each computer its button sends to,
 *  and only the default's library used to be read, so a toggle aimed at
 *  STREAMPC never showed what STREAMPC holds (audit 2026-11-03 A-11). */
const [byBridgeSignal, setByBridgeSignal] = createSignal<Record<string, Record<string, boolean>>>({});

export const toggleStates: Accessor<Record<string, boolean>> = statesSignal;

/** Read one toggle's last-known server state: on `bridgeId` when given (the
 *  button's own computer), else the default's. `undefined` = never polled
 *  yet (no bridge connected, or this id isn't in OPBridge's library) - the
 *  caller decides the pre-connection default (FireButton uses `false`). */
export function toggleState(id: string, bridgeId?: string): boolean | undefined {
  if (bridgeId) return byBridgeSignal()[bridgeId]?.[id];
  return statesSignal()[id];
}

/** Called by {@link ../opbridge/toggleStatePoll.ts} on every poll tick, and
 *  by a successful flip so the local mirror updates without waiting for the
 *  next poll. */
export function recordToggleState(id: string, state: boolean, bridgeId?: string): void {
  setStatesSignal((prev) => (prev[id] === state ? prev : { ...prev, [id]: state }));
  if (bridgeId) {
    setByBridgeSignal((prev) => (prev[bridgeId]?.[id] === state ? prev : { ...prev, [bridgeId]: { ...(prev[bridgeId] ?? {}), [id]: state } }));
  }
}

/** Replace the whole mirror from a fresh `GET /toggles/{consumer}` snapshot.
 *  Ids no longer present in the library are dropped (not left stale) —
 *  matches the "never lose state, never assert stale state either" pattern
 *  the sequencer/macro pollers use via their own seen-id bookkeeping. */
export function recordToggleLibrary(toggles: Array<{ id: string; state: boolean }>, bridgeId?: string, isDefault = true): void {
  const next: Record<string, boolean> = {};
  for (const t of toggles) next[t.id] = t.state;
  if (isDefault) setStatesSignal(next);
  if (bridgeId) setByBridgeSignal((prev) => ({ ...prev, [bridgeId]: next }));
}
